/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { DateTime } from 'luxon';
import { useGetRoutingRequest } from '../../hooks/useRequestManager';
import { useContext } from 'react';
import { AccessContext } from '../DecisionViewer';
import { Callout, Link, Text } from '@cimpress-ui/react';
import { getLastRetryDate } from '../../helpers/errorStatus';
import { Problem } from '../../types';

const noOptionsWrapperStyles = css`
  overflow-y: scroll;

  > div:not(.crc-alert) {
    h3 {
      margin: 2.24rem 0 1.6rem;
      font-size: 1.6rem;
      font-weight: 600;
    }

    p {
      margin: 1.6rem 0;
    }

    p:last-of-type {
      margin: 1.6rem 0 0;
    }

    p:first-of-type {
      margin: 0 0 1.6rem;
    }
  }

  .halfWeight {
    > {
      h3 {
        font-size: 1.2rem;
        margin: 1.5rem 0 1rem;
        font-weight: 600;
      }

      p {
        margin: 1rem 0;
      }

      p:last-of-type {
        margin: 1rem 0 0;
      }

      p:first-of-type {
        margin: 0 0 1rem;
      }
    }
  }
`;

export const NoOptionsNetworkError = ({
  routingProblem,
  routingRequestUrl,
  halfWeight,
}: {
  routingProblem: Problem | undefined;
  routingRequestUrl: string | undefined;
  halfWeight?: boolean;
}) => {
  const { accessToken } = useContext(AccessContext);

  const { data: routingRequestData, isLoading: isLoadingRoutingRequest } = useGetRoutingRequest({
    accessToken,
    routingRequestUrl,
  });

  if (isLoadingRoutingRequest || !routingProblem) {
    return null;
  }

  const lastRetryDate = getLastRetryDate(routingRequestData?.routingRequest);

  const attemptedRetriesSentence = routingRequestData && routingRequestData.routingRequest && lastRetryDate && (
    <Text as="p" data-testid="attempted-retries-sentence">
      The routing request was last tried on {DateTime.fromISO(lastRetryDate).toLocaleString(DateTime.DATE_HUGE)} at{' '}
      {DateTime.fromISO(lastRetryDate).toLocaleString(DateTime.TIME_SIMPLE)} and the request has been retried{' '}
      {routingRequestData.routingRequest.attemptsCount} times.
    </Text>
  );

  return (
    <div css={noOptionsWrapperStyles} data-testid="no-options-network-error">
      <Callout title={routingProblem.title} tone="critical" isDismissible={false}>
        <Text as="p" UNSAFE_style={{ wordBreak: 'break-word' }}>
          {routingProblem.detail}
        </Text>
      </Callout>
      <div className={halfWeight ? 'halfWeight' : undefined}>
        <Text as="h3">Network Error</Text>
        <Text as="p">
          We encountered a network error while attempting to retrieve any available Fulfillment Options for routing this
          item. This indicates that Routing or dependent services are experiencing technical difficulties.
        </Text>

        {attemptedRetriesSentence}

        <Text as="p">
          Requests stuck in routing are retried multiple times but may not succeed without manual intervention. For
          questions or status updates, please reach out to MCP Platform Support for assistance in the{' '}
          <Link href="https://cimpress.slack.com/archives/C7CNT9E2Z" target="_blank" rel="noreferrer">
            #mcp-platform-support
          </Link>{' '}
          Slack channel.
        </Text>
      </div>
    </div>
  );
};
